import Heading from '../../../components/typography';
import { COLORS } from '../../../constants/style/color';
import { PageHeader } from '../../../constants/style/common.styled';
import { FMVContainer } from '../fmv-configuration/fmv.styled';
import {
  useGetMoneyEntitlementExistingSetup,
  usePutMoneyEntitlements,
  useWealthlaneAwardSettlementApi,
} from '../../../hooks';
import Spinners from '../../../components/spinner';
import FormSelect from '../../../components/dropdown/formSelect';
import { booleanYesNo } from '../../../constants/common';
import { Formik } from 'formik';
import { isEqual } from 'lodash';
import Button from '../../../components/button';
import { useEffect, useState } from 'react';

interface MoneyEntitlementMethod {
  transferTypeId?: number;
  displayName?: string | null;
  isEnabled: boolean;
}

interface MoneyMovementFormValues {
  methods: MoneyEntitlementMethod[];
}

const initialFormValues: MoneyMovementFormValues = {
  methods: [],
};

const MoneyMovementConfig = (): JSX.Element => {
  const { handleRequest } = useWealthlaneAwardSettlementApi();
  const { data: existingSetup, isLoading, refetch } = useGetMoneyEntitlementExistingSetup(handleRequest);
  const { mutate, isLoading: isSaving } = usePutMoneyEntitlements(handleRequest);
  const [initialValues, setInitialValues] = useState<MoneyMovementFormValues>(initialFormValues);

  useEffect(() => {
    if (existingSetup) {
      setInitialValues({
        methods: existingSetup.map((item) => ({
          transferTypeId: item.transferTypeId,
          displayName: item.displayName,
          isEnabled: item.isEnabled ?? false,
        })),
      });
    }
  }, [existingSetup]);

  return (
    <>
      <PageHeader>
        <div className='flex items-center gap-2'>
          <Heading variant='h3' title='Money Movement Configuration' />
          <span
            className='material-symbols-outlined cursor-pointer'
            style={{ fontSize: 20, color: COLORS.Gray }}
            title='Refresh'
            onClick={() => refetch()}>
            refresh
          </span>
        </div>
        <Heading
          variant='body-s'
          title='Configure money transfer type settings for your organization.'
          color={COLORS.Gray}
        />
      </PageHeader>

      {isLoading ? (
        <div className='flex justify-center items-center py-10'>
          <Spinners />
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={(values) => {
            const payload = values.methods.map((item) => ({
              transferTypeId: item.transferTypeId,
              displayName: item.displayName,
              isEnabled: item.isEnabled,
            }));
            mutate(payload);
          }}>
          {({ values, errors, touched, setFieldValue, handleSubmit }) => (
            <div className='flex flex-col gap-4'>
              <FMVContainer>
                <div className='grid grid-cols-2 lg:grid-cols-12 md:grid-cols-2 gap-x-8 items-center mb-4'>
                  <Heading
                    variant='h6'
                    title='Money Transfer Type'
                    className='lg:col-span-4 col-span-1'
                  />
                  <Heading variant='h6' title='Enable' className='lg:col-span-4 col-span-1' />
                </div>
                {values.methods.map((item, index) => (
                  <div className='grid grid-cols-2 lg:grid-cols-12 md:grid-cols-2 gap-x-8 items-center'>
                    <div className='lg:col-span-4 col-span-1'>
                      <Heading variant='body-s' title={item.displayName ?? ''} />
                    </div>
                    <div className='lg:col-span-4 col-span-1'>
                      <FormSelect
                        label=''
                        name={`methods.${index}.isEnabled`}
                        onChange={(value: any) => {
                          setFieldValue(`methods.${index}.isEnabled`, value);
                        }}
                        errors={errors}
                        touched={touched}
                        options={booleanYesNo}
                        values={values}
                        arrValue={values.methods[index].isEnabled}
                        setFieldValue={setFieldValue}
                        containerClassName='formGroup'
                      />
                    </div>
                  </div>
                ))}

                <div className='flex justify-end gap-x-5'>
                  <Button
                    title='Save'
                    type='button'
                    variant='primary'
                    loading={isSaving}
                    disabled={isSaving || isEqual(values, initialValues)}
                    onClick={() => handleSubmit()}
                    icon='check_circle'
                  />
                </div>
              </FMVContainer>
            </div>
          )}
        </Formik>
      )}
    </>
  );
};

export default MoneyMovementConfig;
